import { WindowService } from './../../services/window.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit } from '@angular/core';
import { Bounty } from 'src/app/interfaces/Bounty';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { BountyService } from 'src/app/services/bounty.service';
import {
  GameMode,
  GAME_MODES,
  transformFormDataToBountyRequest,
  transformValidationErrors,
  durationOptions
} from '../create-bounty/create-bounty.component';

@Component({
  selector: 'app-edit-bounty',
  templateUrl: './edit-bounty.component.html',
  styleUrls: ['./edit-bounty.component.scss'],
})
export class EditBountyComponent implements OnInit {
  public bounty: Bounty;
  public submitting = false;
  public success = false;
  public errors: string[];
  public form: FormGroup;
  public gameMode: GameMode;
  public gameModes = GAME_MODES;
  public submitFailed = false;
  public addDuration = false;

  public tournamentName: string;
  public tournamentPlace: number;
  public durationOptions = durationOptions;
  public duration: { name: string; id: number } = this.durationOptions.find(o => o.name === '30 days');

  constructor(
    private bountyService: BountyService,
    private route: ActivatedRoute,
    private windowService: WindowService,
    private router: Router,
  ) { }

  ngOnInit() {
    this.form = new FormGroup({
      gameMode: new FormControl(this.gameMode, [Validators.required]),
      tournamentName: new FormControl(this.tournamentName),
      tournamentPlace: new FormControl(this.tournamentPlace),
      addDuration: new FormControl(this.addDuration),
      duration: new FormControl(this.duration),
    });
    const preRenderedData = this.windowService.appData;

    if (preRenderedData) {
      this.mapAppDataToState(preRenderedData);
    } else {
      this.fetchBounty();
    }
  }

  private async fetchBounty() {
    const id = this.route.snapshot.paramMap.get('id');
    const data = await this.bountyService.get(id);
    this.mapAppDataToState(data);
  }

  private mapAppDataToState({ bounty }) {
    this.bounty = bounty;
    this.form.patchValue({
      gameMode: bounty.mode,
      tournamentName: bounty.tournament_name,
      tournamentPlace: bounty.tournament_place,
    });
  }

  public get canChangeReward() {
    return this.bounty.payment_type !== 'pool';
  }

  public async submitForm() {
    if (!this.form.valid) {
      this.submitFailed = true;

      return;
    }

    this.submitting = true;
    this.errors = undefined;
    this.success = false;
    const formData = this.form.value;

    const parsedFormData = transformFormDataToBountyRequest(formData);

    if (!this.form.get('addDuration').value) {
      parsedFormData.duration = undefined; // shouldn't be set if option is false
    }

    parsedFormData.addDuration = undefined; // not needed

    if (!this.canChangeReward && parsedFormData.reward !== this.bounty.reward) {
      this.errors = ['The reward cannot be changed on a pool bounty'];
      this.submitFailed = true;
      this.submitting = false;
      return;
    }

    try {
      const { bounty } = await this.bountyService.update(
        parsedFormData,
        this.bounty.id,
      );
      this.mapAppDataToState({ bounty });
      this.success = true;
      this.router.navigate(['/bounties', this.bounty.id]);
    } catch (e) {
      if (e.error) {
        if (e.error.errors) {
          this.errors = transformValidationErrors(e.error.errors);
        } else if (e.error.error) {
          this.errors = [e.error.error];
        }
      } else {
        const error =
          e.status === 401
            ? 'You are not authorized to do this. Are you logged in?'
            : 'A server error occured';
        this.errors = [error];
      }
      this.submitting = false;
    }
  }

  public hasError(controlKey: string) {
    const control = this.form.get(controlKey);
    const touchedOrSubmitted = !control.pristine || this.submitFailed;
    return control.errors && touchedOrSubmitted;
  }

  get labelClass() {
    return this.bountyService.getBountyLabelClass(this.bounty);
  }
}
